import { MapUtils } from '../../utility/map-utils.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
  cloneDataOrNull, isSameData,
} from '../dto/dto.js';
import { ApplicationFeeProtocol } from './application-fee/application-fee-protocol.js';
import { PaymentAccountRef } from './payment-account-ref.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { VersionRange } from '../core/version-range.js';
import { LocalizedRichDescription } from './localized-rich-description.js';

export class ProductSellingSettings extends DataModel {
  static readonly TYPE_ID: string = '25084de4165c4dd686f34888e347d99e';
  static readonly SCHEMA_VERSION: number = 3;

  private constructor(
    public readonly businessRef: BusinessModuleRef,
    public readonly paymentAccount: PaymentAccountRef | null,
    public readonly feeProtocol: ApplicationFeeProtocol | null,
    public readonly productsDescription: LocalizedRichDescription | null,
    public readonly qrCodePrefix: string | null,
  ) {
    super();
  }

  get $typeId(): string {
    return ProductSellingSettings.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, ProductSellingSettings.SCHEMA_VERSION);
  }

  static create(args: {
    businessRef: BusinessModuleRef,
    paymentAccount: PaymentAccountRef | null,
    feeProtocol: ApplicationFeeProtocol | null,
    productsDescription: LocalizedRichDescription | null,
    qrCodePrefix: string | null,
  }): ProductSellingSettings {
    return new ProductSellingSettings(
        args.businessRef,
        args.paymentAccount,
        args.feeProtocol,
        args.productsDescription,
        args.qrCodePrefix,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): ProductSellingSettings {
    return ProductSellingSettings.create({
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      paymentAccount: context.fromDataOrNull<PaymentAccountRef>(MapUtils.getRecordOrNull(map, 'paymentAccount')),
      feeProtocol: context.fromDataOrNull<ApplicationFeeProtocol>(MapUtils.getRecordOrNull(map, 'feeProtocol')),
      productsDescription: context.fromDataOrNull<LocalizedRichDescription>(MapUtils.getRecordOrNull(map, 'productsDescription')),
      qrCodePrefix: MapUtils.getStringOrNull(map, 'qrCodePrefix'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'businessRef': this.businessRef.encode(),
      'paymentAccount': context.toDataOrNull(this.paymentAccount),
      'feeProtocol': context.toDataOrNull(this.feeProtocol),
      'productsDescription': context.toDataOrNull(this.productsDescription),
      'qrCodePrefix': this.qrCodePrefix,
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    businessRef = this.businessRef.clone(),
    paymentAccount = cloneDataOrNull(this.paymentAccount),
    feeProtocol = cloneDataOrNull(this.feeProtocol),
    productsDescription = cloneDataOrNull(this.productsDescription),
    qrCodePrefix = this.qrCodePrefix,
  }: {
    businessRef?: BusinessModuleRef,
    paymentAccount?: PaymentAccountRef | null,
    feeProtocol?: ApplicationFeeProtocol | null,
    productsDescription?: LocalizedRichDescription | null,
    qrCodePrefix?: string | null,
  } = {}): this {
    return ProductSellingSettings.create({
      businessRef,
      paymentAccount,
      feeProtocol,
      productsDescription,
      qrCodePrefix,
    }) as this;
  }

  isSame(other: ProductSellingSettings): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.businessRef, this.businessRef) &&
      isSameData(other.paymentAccount, this.paymentAccount) &&
      isSameData(other.feeProtocol, this.feeProtocol) &&
      isSameData(other.productsDescription, this.productsDescription) &&
      other.qrCodePrefix === this.qrCodePrefix;
  }
}

export class ProductSellingSettingsBuilder extends DataModelBuilder<ProductSellingSettings> {
  build(context: SerializationContext, map: SerializedData): ProductSellingSettings {
    return ProductSellingSettings.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(ProductSellingSettings.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(ProductSellingSettings.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version we added [qrCodePrefix] of type [string | null]
    if(version < 2) version = 2;

    // In this version we added [productsDescription] of type [LocalizedRichDescription | null]
    if(version < 3) version = 3;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
